import { useEffect } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { FinancialTermsHero } from './components/FinancialTermsHero';
import { UnderstandingValuation } from './components/UnderstandingValuation';
import { KeyFinancialTerms } from './components/KeyFinancialTerms';
import { TransactionCosts } from './components/TransactionCosts';
import { NetEarningsCalculation } from './components/NetEarningsCalculation';
import { TaxImplicationsTerms } from './components/TaxImplicationsTerms';
import { PropertyValuationMethods } from './components/PropertyValuationMethods';
import { ClosingCostsBreakdown } from './components/ClosingCostsBreakdown';
import { EquityAndFinancing } from './components/EquityAndFinancing';
import { LegalDocumentTerms } from './components/LegalDocumentTerms';
import { MarketAnalysisTerms } from './components/MarketAnalysisTerms';
import { ExchangeStrategies } from './components/ExchangeStrategies';
import { FinancialTermsFinalCTA } from './components/FinancialTermsFinalCTA';

export default function ApartmentBuildingFinancialTerms() {
  useEffect(() => {
    // Set document title
    document.title = 'Key Financial Terms for Selling Apartment Buildings - Complete Guide 2024';
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master essential financial terms for selling apartment buildings. Learn NOI, cap rates, valuation methods, transaction costs, and net earnings calculations. Complete guide to apartment building sale terminology and financial concepts.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master essential financial terms for selling apartment buildings. Learn NOI, cap rates, valuation methods, transaction costs, and net earnings calculations. Complete guide to apartment building sale terminology and financial concepts.';
      document.head.appendChild(meta);
    }

    // Set meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'apartment building financial terms, NOI, cap rate, apartment valuation, transaction costs, net sale proceeds, 1031 exchange, capital gains tax, closing costs, real estate commissions');
    } else {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      metaKeywords.setAttribute('content', 'apartment building financial terms, NOI, cap rate, apartment valuation, transaction costs, net sale proceeds, 1031 exchange, capital gains tax, closing costs, real estate commissions');
      document.head.appendChild(metaKeywords);
    }

    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://sellapartmentscash.com/apartment-building-financial-terms');
    } else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', 'https://sellapartmentscash.com/apartment-building-financial-terms');
      document.head.appendChild(canonical);
    }

    // Add structured data for Article
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Key Financial Terms for Selling Apartment Buildings - Complete Guide 2024",
      "description": "Comprehensive guide to essential financial terms, valuation methods, and cost calculations for apartment building sellers.",
      "author": {
        "@type": "Organization",
        "name": "Sell Apartments Cash"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Sell Apartments Cash",
        "logo": {
          "@type": "ImageObject",
          "url": "https://sellapartmentscash.com/logo.png"
        }
      },
      "datePublished": "2024-01-15",
      "dateModified": new Date().toISOString().split('T')[0],
      "mainEntity": {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What is NOI in apartment building sales?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "NOI (Net Operating Income) is the total rental income minus operating expenses, excluding mortgage payments and depreciation. It's the key metric buyers use to evaluate apartment building profitability."
            }
          },
          {
            "@type": "Question", 
            "name": "How is cap rate calculated for apartment buildings?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Cap rate is calculated by dividing the Net Operating Income (NOI) by the property's market value or purchase price. For example, if NOI is $100,000 and the property value is $1,000,000, the cap rate is 10%."
            }
          },
          {
            "@type": "Question",
            "name": "What are typical closing costs when selling an apartment building?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Closing costs typically range from 2-8% of the sale price and include real estate commissions (6-10%), title insurance, transfer taxes, attorney fees, and escrow costs."
            }
          },
          {
            "@type": "Question",
            "name": "How does a 1031 exchange work for apartment building sales?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "A 1031 exchange allows you to defer capital gains taxes by reinvesting the proceeds from your apartment building sale into a like-kind property within specific timeframes (45 days to identify, 180 days to close)."
            }
          }
        ]
      }
    };

    // Add structured data script
    let structuredDataScript = document.querySelector('script[type="application/ld+json"]');
    if (structuredDataScript) {
      structuredDataScript.textContent = JSON.stringify(structuredData);
    } else {
      structuredDataScript = document.createElement('script');
      structuredDataScript.type = 'application/ld+json';
      structuredDataScript.textContent = JSON.stringify(structuredData);
      document.head.appendChild(structuredDataScript);
    }

    // Cleanup function
    return () => {
      const elementsToRemove = [
        document.querySelector('meta[name="description"]'),
        document.querySelector('meta[name="keywords"]'),
        document.querySelector('link[rel="canonical"]'),
        document.querySelector('script[type="application/ld+json"]')
      ].filter(element => {
        if (!element) return false;
        const content = element.getAttribute('content') || element.getAttribute('href') || element.textContent || '';
        return content.includes('financial terms') || content.includes('apartment-building-financial-terms');
      });
      
      elementsToRemove.forEach(element => element?.remove());
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <FinancialTermsHero />
      <UnderstandingValuation />
      <KeyFinancialTerms />
      <PropertyValuationMethods />
      <TransactionCosts />
      <ClosingCostsBreakdown />
      <NetEarningsCalculation />
      <TaxImplicationsTerms />
      <EquityAndFinancing />
      <LegalDocumentTerms />
      <MarketAnalysisTerms />
      <ExchangeStrategies />
      <FinancialTermsFinalCTA />
      <Footer />
    </div>
  );
}
